import { useEffect, useRef, useState } from "react";
import { approvePendingSite, listPendingSites, rejectPendingSite } from "../../services/siteManagement/siteManagement.service";
import { useTreeRefresh } from "../../contexts/TreeRefreshContext";
import { getPendingErrorMessage, getPendingList, validatePendingAction } from "./pendingSites";
import type { PendingAction, PendingMode, PendingSite } from "./pendingSites";

export function usePendingSites() {
    const [sites, setSites] = useState<PendingSite[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [operationError, setOperationError] = useState<string | null>(null);
    const [success, setSuccess] = useState<{ message: string } | null>(null);
    const { refreshTree, refreshBureauData } = useTreeRefresh();
    const requestId = useRef(0);
    const mutationInFlight = useRef(false);
    const completedIds = useRef(new Set<number>());

    useEffect(() => {
        let mounted = true;
        const current = ++requestId.current;
        const load = async () => {
            try {
                const response = await listPendingSites();
                if (!response.success || !response.data) throw new Error(response.error?.message || "Failed to load pending sites");
                if (mounted && current === requestId.current) setSites(getPendingList(response.data));
            } catch (error: unknown) {
                if (mounted) setError(getPendingErrorMessage(error, "Failed to load pending sites"));
            } finally {
                if (mounted) setLoading(false);
            }
        };
        void load();
        return () => { mounted = false; requestId.current += 1; };
    }, []);

    useEffect(() => {
        if (!success) return;
        const timeout = setTimeout(() => setSuccess(null), 6000);
        return () => clearTimeout(timeout);
    }, [success]);

    const process = async (items: PendingAction[], mode: PendingMode): Promise<number[]> => {
        if (mutationInFlight.current) return [];
        mutationInFlight.current = true;
        setSubmitting(true);
        setError(null);
        setOperationError(null);
        setSuccess(null);
        const completed = new Set<number>();
        const failures: string[] = [];
        try {
            for (const item of items) {
                if (completed.has(item.s.id)) continue;
                try {
                    const guid = validatePendingAction(item, mode);
                    const response = mode === "approve"
                        ? await approvePendingSite({ customerId: item.customerId!, pendingSiteRequestGuid: guid })
                        : await rejectPendingSite({
                            pendingSiteRequestGuid: guid, reason: item.reason?.trim(),
                            ...(item.customerId ? { customerId: item.customerId } : {}),
                        });
                    if (!response.success) throw new Error(response.error?.message || `${mode === "approve" ? "Approve" : "Reject"} failed`);
                    completed.add(item.s.id);
                    completedIds.current.add(item.s.id);
                    setSites(previous => previous.filter(site => site.id !== item.s.id));
                } catch (error: unknown) {
                    failures.push(`${item.s.siteName || item.s.siteIdentifier || item.s.id}: ${getPendingErrorMessage(error, "Pending site action failed")}`);
                }
            }
            const refreshErrors: string[] = [];
            if (completed.size) {
                // Keep the backend processing delay, once per batch rather than once per site.
                await new Promise<void>(resolve => setTimeout(resolve, 500));
                await Promise.all([
                    (async () => {
                        try {
                            const response = await listPendingSites(true);
                            if (!response.success || !response.data) throw new Error(response.error?.message || "Failed to refresh pending sites");
                            setSites(getPendingList(response.data).filter(site => !completedIds.current.has(site.id)));
                        } catch (error: unknown) {
                            refreshErrors.push(getPendingErrorMessage(error, "Failed to refresh pending sites"));
                        }
                    })(),
                    (async () => {
                        try { await refreshTree(); }
                        catch (error: unknown) { refreshErrors.push(getPendingErrorMessage(error, "Failed to refresh site tree")); }
                    })(),
                    (async () => {
                        try { await refreshBureauData?.(); }
                        catch (error: unknown) { refreshErrors.push(getPendingErrorMessage(error, "Failed to refresh bureau data")); }
                    })(),
                ]);
            }
            if (failures.length) setOperationError(failures.join("\n"));
            if (failures.length || refreshErrors.length) {
                const prefix = completed.size ? `${completed.size} site(s) ${mode === "approve" ? "approved" : "rejected"}, but some updates failed: ` : "";
                setError(prefix + [...failures, ...refreshErrors].join("\n"));
            } else if (completed.size) {
                const action = mode === "approve" ? "approved" : "rejected";
                setSuccess({ message: completed.size === 1 ? `Site ${action}` : `${completed.size} sites ${action}` });
            }
            return [...completed];
        } finally {
            mutationInFlight.current = false;
            setSubmitting(false);
        }
    };

    return { sites, loading, submitting, error, operationError, success, setError, setOperationError, setSuccess, process };
}
